import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { User } from '../models/user.model';
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { ObjectHelper } from "../helpers/object.helper";
import { Functions, httpsCallable, HttpsCallableResult } from "@angular/fire/functions";

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private injector = inject(Injector);

  /**
   * Listen to a user document so later edits (roles, production sites) are received without a refresh
   * @param uid The user id
   * @param onUser Called with the user each time the document changes
   * @returns Unsubscribe function
   */
  public watchUser(uid: string, onUser: (user: User) => void): Unsubscribe {
    const ref = doc(this.firestore, 'users', uid).withConverter(User.converter);
    return runInInjectionContext(this.injector, () => onSnapshot(ref, snapshot => {
      if (snapshot.exists()) {
        onUser(snapshot.data());
      }
    }, error => console.error(error)));
  }

  /**
   * Get a firebase user
   * @param uid The user id
   * @returns The user or null
   */
  public async getUserByUid(uid: string): Promise<User> {
    const ref = doc(this.firestore, 'users', uid).withConverter(User.converter);
    const docSnap = await getDoc(ref);
    if (!docSnap.exists()) {
      return null;
    }
    return docSnap.data();
  }

  /**
   * Save a user in firebase
   * @param id the id of the user
   * @param user The field of the user to update
   * @returns Update doc result
   */
  public saveUser(id: string, user: Partial<User>): Promise<void> {
    const ref = doc(this.firestore, 'users', id)
      .withConverter(User.converter);
    return updateDoc(ref, ObjectHelper.anonymize(user));
  }

  /**
   * Get all users
   * @returns A query to listen users changes
   */
  public async listUsers(): Promise<User[]> {
    const q = query(collection(this.firestore, 'users'),
      orderBy('lastName', 'asc'))
      .withConverter(User.converter);
    return getDocs(q).then(snapshot => {
      if (snapshot.empty) {
        return [];
      }
      return snapshot.docs.map(doc => doc.data());
    }).catch(err => {
      console.error(err);
      throw err;
    });
  }

  /**
   * Get all users
   * @param uIds The ids to fetch
   * @returns A query to listen users changes
   */
  public async getUsers(uIds: string[]): Promise<User[]> {
    const promises: Promise<User[]>[] = []
    const uniqueIds = [...new Set(uIds)];
    const ref = collection(this.firestore, 'users');
    for (let i = 0; i < uniqueIds.length; i += 10) {
      const idList = uniqueIds.slice(i, i + 10);
      const q = query(ref, where('id', 'in', idList))
        .withConverter(User.converter);
      const promise = getDocs(q)
        .then(snapshot => snapshot.docs.map(doc => doc.data()));
      promises.push(promise);
    }
    return Promise.all(promises)
      .then(arrayOfUsers => {
        const users = arrayOfUsers.flat();
        users.sort((a, b) => a.displayName.localeCompare(b.displayName))
        return users;
      })
  }

  /**
   * Find all user having access to that customer
   * @param customerId The user customer ids
   * @returns The customers
   */
  async byCustomerId(customerId: string): Promise<User[]> {
    const q = query(
      collection(this.firestore, 'users'),
      where('customerIds', 'array-contains', customerId),
    ).withConverter(User.converter)
    return getDocs(q).then(snapshot => snapshot.docs.map(doc => doc.data()));
  }

  async updateCustomerUsers(fromUserId: string, fromOrgId: string, customerId: string, userIdsToRemove: string[], 
    userEmailsToAdd: string[]): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ fromUserId: string, fromOrgId: string, customerId: string, userIdsToRemove: string[],
      userEmailsToAdd: string[] }, void>(this.functions, 'update_customer_users_gen2')(
        { fromUserId, fromOrgId, customerId, userIdsToRemove, userEmailsToAdd });
  }

  async deleteCustomerInvite(id: string): Promise<void> {
    await deleteDoc(doc(this.firestore, 'invitation-customer', id));
  }
}
